// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PieChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import DefaultChartOptionsBuilder from './Izenda.DefaultChartOptionsBuilder';
import jsFormatService from 'share/Izenda.JsFormatService';
import ChartDataParser from 'share/utils/Izenda.ChartDataParser';
import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import constants from 'logic/data/Izenda.ReportPartChartConstants';
import {
  size,
  omit,
  merge,
  assign,
  get,
  isArray,
  filter,
  flow,
  has,
  find,
  isNil,
  isNull,
  isDate,
  findIndex,
  first
} from 'lodash/fp';
import { exportClass } from 'share/Izenda.ServiceProvider';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import { isChartType } from 'logic/data/Izenda.ReportPartChartConstants';
import { CUSTOM_FORMAT_PREFIX } from 'logic/data/Izenda.ReportPartFieldConstants';

const showText = HighchartUtils.showText;

@exportClass('PieChartOptionsBuilder')
export default class PieChartOptionsBuilder extends DefaultChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);

    this.onClickOnPoint = this._onClickOnPoint.bind(this);
  }

  /**
   * Build formatted label
   * @param {Object} point
   * @param {Array<Object>} series
   * @param {Number} percentage
   * @param {Boolean} forOthers
   */
  getDataLabelFormatProcess(point: any, series: Array<any>, percentage: Number, forOthers: Boolean, rawText: Boolean) {
    const pointOpts = get('options', point);
    const dataLabel = [];
    const userOptions = series.userOptions || {};

    const pointFormatedName = find(val => !isNil(val), [pointOpts.key, pointOpts.name, point.x]);
    let pointName = find(val => !isNil(val), [pointOpts.key, pointOpts.originalNameValue, pointOpts.name, point.x]);

    // For null value , just show 'null' text on chart
    isNull(pointOpts.name) && (pointName = 'null');

    const labelNameConfig = this.izendaSeriesConfig[userOptions.labelName] || this.izendaSeriesConfig[point.labelName];
    const dataType = get('fieldElement.properties.dataFormattings.functionInfo.formatDataType', labelNameConfig);

    const jsFormatStringLabelName = get(
      'fieldElement.properties.dataFormattings.format.jsFormatString',
      labelNameConfig
    );

    let sliceLabel;
    if (jsFormatStringLabelName) {
      let pointData;
      if (isNil(pointName)) {
        pointData = null;
      } else {
        if (isDate(pointName) && dataType === 'Datetime') {
          pointData = new Date(pointName);
        } else {
          pointData = pointName;
        }
      }

      const jsFormatId = get('fieldElement.properties.dataFormattings.format.formatId', labelNameConfig);

      sliceLabel = jsFormatService.format(jsFormatId, pointData, pointName);
    }

    const sliceFieldValue = {
      value: sliceLabel || pointFormatedName,
      originalValue: pointName
    };

    sliceLabel = this.dataParser.getFormatData(sliceFieldValue, labelNameConfig, {
      element: get('nameField', point) || get('nameField', userOptions),
      record: get('record', point) || get('record', pointOpts),
      sliceFieldValue
    });

    (!!this.izendaOptions.showSliceLabel || forOthers) &&
      dataLabel.push(
        forOthers || rawText
          ? showText(sliceLabel, false)
          : String.format('<span class="point-label">{0}</span>', showText(sliceLabel))
      );

    // data label
    const valueLabel = this.getValueLabel(get('options', point), userOptions, pointFormatedName, pointName);

    !!this.izendaOptions.showValueLabel &&
      dataLabel.push(
        forOthers || rawText
          ? ` ${showText(valueLabel, false)}`
          : String.format('<span class="point-value">  {0}</span>', showText(valueLabel))
      );

    // percentage
    !!this.izendaOptions.showPercentage &&
      dataLabel.push(
        forOthers || rawText
          ? ` (${percentage.toFixed(2)}%)`
          : String.format('<span class="point-percent">  ({0}%)</span>', percentage.toFixed(2))
      );

    return forOthers || rawText
      ? dataLabel.join(' ')
      : String.format('<span class="pie-serie-label">{0}</span>', dataLabel.join(' '));
  }

  /**
   * Get formatted value label
   * @param {Object} pointOpts 
   * @param {Object} userOptions 
   * @param {String} pointFormatedName 
   * @param {String} pointName 
   */
  getValueLabel(pointOpts: Object, userOptions: Object, pointFormatedName: String, pointName: String) {
    let customFormat = this.getCustomFormat(pointOpts, userOptions.yAxisField);

    const nameConfig = this.izendaSeriesConfig[userOptions.name] || this.izendaSeriesConfig[pointOpts.name];
    const jsFormatString = get('fieldElement.properties.dataFormattings.format.jsFormatString', nameConfig);

    const jsFormatId = get('fieldElement.properties.dataFormattings.format.formatId', nameConfig);

    if (jsFormatString) {
      customFormat = jsFormatService.format(jsFormatId, pointOpts.y || pointOpts.value);
    }

    const fieldValue = {
      value: customFormat || pointOpts.y || pointOpts.value,
      originalValue: pointOpts.y || pointOpts.value
    };

    return this.dataParser.getFormatData(fieldValue, nameConfig, {
      element: get('yAxisField', pointOpts) || get('yAxisField', userOptions),
      record: get('record', pointOpts) || get('record', pointOpts),
      nameFieldValue: {
        value: pointFormatedName,
        originalValue: pointName
      }
    });
  }

  // Get custom format for data label
  getCustomFormat(pointOpts, yAxisField) {
    let result = null;
    const columnName = get('columnName', yAxisField);
    const hasCustomFormat = flow(
      get('record'),
      has(CUSTOM_FORMAT_PREFIX + columnName)
    )(pointOpts);

    if (hasCustomFormat) {
      result = flow(
        get('record'),
        get(columnName)
      )(pointOpts);
    }

    return result;
  }
  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   */
  buildOptionsByType(visualType: string, userOptions, dataParser): Object {
    this.dataParser = dataParser;
    let chartOptions = super.buildOptionsByType(visualType, userOptions, dataParser);

    const izendaOptions = userOptions.izendaOptions || {};
    this.izendaOptions = izendaOptions;
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
    this.izendaSeriesConfig = izendaSeriesConfig;
    const isShowDataLabels =
      izendaOptions.showValueLabel || izendaOptions.showSliceLabel || izendaOptions.showPercentage || false;
    const chartSizePercent = 100;
    const chartInnerSizePercent = Math.round(chartSizePercent / 2);

    const xAxis = chartOptions.xAxis;
    const xAxisName = xAxis.axisName;
    const getDataLabelFormatProcess = this.getDataLabelFormatProcess.bind(this);
    const getValueLabel = this.getValueLabel.bind(this);

    if (userOptions.series) {
      chartOptions = omit('series', chartOptions);
    }

    const orgSeriesLength = (userOptions.series) ? userOptions.series.length : 0 ;
    userOptions.series = removeNegativeData(userOptions.series || chartOptions.series);

    // If all series are removed, throw error to show message "Can't draw chart"
    if (orgSeriesLength > 0 && userOptions.series.length === 0) {
      throw new Error('All series are negative or null!');
    }

    if (izendaOptions.bottomXpercent) {
      applyBottomPercent(izendaOptions);
    }

    chartOptions = merge(chartOptions, {
      chart: {
        type: 'pie'
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: isShowDataLabels,
            formatter: getDataLabelFormat,
            useHTML: true
          }
        },
        pie: {
          showInLegend: true,
          size: `${chartSizePercent}%`,
          dataLabels: {
            padding: 0
          }
        }
      },
      commonActions: {
        getDataLabelFormatProcess,
        getValueLabel
      },
      userOptions
    });

    if (izendaOptions.showPercentage) {
      chartOptions = assign(chartOptions, {
        tooltip: {
          pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
        }
      });
    }

    // add sub-report to x-axis
    !(userOptions.izMultiLevel && isChartType(this.chartOptions.chartType, CHART_TYPE.TreeMap)) &&
      HighchartUtils.addPointActionLink(
        true,
        userOptions,
        get('plotOptions.series.dataLabels', chartOptions),
        xAxisName,
        get(xAxisName, izendaSeriesConfig)
      );

    if (izendaOptions.pieChartStyle === 'ring') {
      return $izenda.extend(true, chartOptions, {
        plotOptions: {
          pie: {
            innerSize: chartInnerSizePercent + '%'
          }
        }
      });
    } else if (izendaOptions.pieChartStyle === 'semiCirle') {
      return $izenda.extend(true, chartOptions, {
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 90,
            center: ['50%', '80%'],
            innerSize: chartInnerSizePercent + '%'
          }
        }
      });
    }

    return chartOptions;

    function removeNegativeData(series) {
      if (isArray(series)) {
        return series.filter(serie => {
          serie.data = filter(data => {
            data.value = data.y;
            return get('y', data) > 0;
          }, serie.data);

          return size(serie.data);
        });
      } else {
        return series;
      }
    }

    function getDataLabelFormat() {
      return getDataLabelFormatProcess(this.point, this.series, this.percentage);
    }

    function applyBottomPercent(izendaOptions) {
      const series = userOptions.series || chartOptions.series;
      const data = get('[0].data', series);
      const nameFieldColumnName = get('[0].nameField.columnName', series);
      const yAxisFieldColumnName = get('[0].yAxisField.columnName', series);

      if (data) {
        const newData = get(
          'newData',
          ChartDataParser.processDataByBottomPercent(izendaOptions.bottomXpercent, data, 'name', 'y')
        );
        const othersIndex = findIndex(i => i.isComputed, newData);
        if (othersIndex != -1) {
          let otherData = newData[othersIndex];
          otherData.includedItems.forEach(item => {
            item.label = getDataLabelFormatProcess(
              {
                options: item,
                name: item.name,
                value: item.value
              },
              { userOptions: first(series) },
              item.percentage,
              true
            );
          });
          otherData.record = {};
          otherData.record[nameFieldColumnName] = get('name', otherData);
          otherData.record[yAxisFieldColumnName] = get('y', otherData);
        }
        series[0].data = newData;
      }
    }
  }

  /**
   * Handle click event on data point
   * @param {Object} point Data point
   * @param {Object} commonActions Common defined actions on chart
   */
  _onClickOnPoint(point, commonActions) {
    if (point.isComputed) {
      const series = get('series', point);
      const currentPositionInLabels = get('userOptions.nameField.reportPartElm.position', series);
      return commonActions.drillOnOthersPeace(point.includedItems, currentPositionInLabels).then(({ record }) => {
        if (!record) return;
        super.onClickOnPoint(
          {
            series,
            record: record.record,
            options: record,
            name: record.name
          },
          commonActions
        );
      });
    }

    super.onClickOnPoint(point, commonActions);
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.PieChartOptionsBuilder.js